/**
 * O estado das abas do pop-up "Vincular a outra marca" e a tradução
 * dele pro corpo do pedido — separado da tela pra ser testável sem
 * render, como o `bulk-patch.ts` do lote.
 */

import type { LinkMentionsResult, LinkTargetWire } from "@/shared/api/api-client";

/** Uma aba: a marca de destino e a classificação preenchida nela. Tudo
 *  em branco é legítimo — cria a menção sem classificação. */
export type LinkTab = {
  brandId: number;
  polarity: string | null;
  approach: string | null;
  themeIds: number[];
  keyMessageIds: number[];
  spokespersonIds: number[];
};

export const emptyTab = (brandId: number): LinkTab => ({
  brandId,
  polarity: null,
  approach: null,
  themeIds: [],
  keyMessageIds: [],
  spokespersonIds: []
});

/** O corpo que o servidor lê: só o `brand_id` identifica o destino (a
 *  empresa é resolvida lá), e campo em branco não viaja. */
export function buildLinkTargets(tabs: LinkTab[]): LinkTargetWire[] {
  return tabs.map(tab => {
    const target: LinkTargetWire = { brand_id: tab.brandId };
    if (tab.polarity !== null) target.polarity = tab.polarity;
    if (tab.approach !== null) target.approach = tab.approach;
    if (tab.themeIds.length) target.theme_ids = [...new Set(tab.themeIds)];
    if (tab.keyMessageIds.length) target.key_message_ids = [...new Set(tab.keyMessageIds)];
    if (tab.spokespersonIds.length) {
      target.spokesperson_ids = [...new Set(tab.spokespersonIds)];
    }
    return target;
  });
}

function plural(count: number, one: string, many: string): string {
  return `${count.toLocaleString("pt-BR")} ${count === 1 ? one : many}`;
}

/** O aviso depois de vincular. Diz o que foi criado E o que não foi —
 *  "já estava vinculada" não pode parecer que deu certo nem que falhou. */
export function linkResultMessage(result: LinkMentionsResult): string {
  const parts: string[] = [];
  if (result.created > 0) {
    parts.push(plural(result.created, "menção criada", "menções criadas"));
  }
  if (result.already_linked > 0) {
    parts.push(
      plural(result.already_linked, "já estava vinculada", "já estavam vinculadas")
    );
  }
  if (result.already_linked_discarded > 0) {
    parts.push(
      `${plural(
        result.already_linked_discarded,
        "já existe, mas está descartada",
        "já existem, mas estão descartadas"
      )} (restaure pela lista de descartadas)`
    );
  }
  if (result.discarded_sources > 0) {
    parts.push(
      plural(result.discarded_sources, "origem descartada", "origens descartadas")
    );
  }
  return parts.length ? `${parts.join("; ")}.` : "Nada foi vinculado.";
}
